// Enhanced PaymentFormDialog.tsx with comprehensive year-wise fee breakdown
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  DollarSign,
  CreditCard,
  Hash,
  Loader2,
  CheckCircle,
  IndianRupee,
  GraduationCap,
  Calendar,
  BookOpen,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { api } from "@/services/api";

// Types for year-wise fee breakdown
interface YearwiseFeeBreakdown {
  yearNumber: number;
  academicYear: string;
  feeAmount: number;
  discountAmount: number;
  finalAmount: number;
  paidAmount: number;
  dueAmount: number;
  status: 'pending' | 'partial' | 'completed';
  formattedFeeAmount: string;
  formattedDiscountAmount: string;
  formattedFinalAmount: string;
  formattedPaidAmount: string;
  formattedDueAmount: string;
}

interface YearwiseFeeData {
  student: {
    id: string;
    studentId: string;
    rollNumber: string;
    firstName: string;
    lastName: string;
    fatherName: string;
    college: { name: string; code: string };
    courseGroup: { name: string; code: string; durationYears: number };
  };
  yearwiseBreakdown: YearwiseFeeBreakdown[];
  totals: {
    totalFeeAmount: number;
    totalDiscountAmount: number;
    totalFinalAmount: number;
    totalPaidAmount: number;
    totalDueAmount: number;
    formattedTotalFeeAmount: string;
    formattedTotalDiscountAmount: string;
    formattedTotalFinalAmount: string;
    formattedTotalPaidAmount: string;
    formattedTotalDueAmount: string;
  };
  availableAcademicYears: Array<{
    academicYear: string;
    yearNumber: number;
    dueAmount: number;
    formattedDueAmount: string;
  }>;
}

interface PaymentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paymentData: any;
  onSubmit: (data: any) => void;
  loading: boolean;
}

const PaymentFormDialog: React.FC<PaymentFormDialogProps> = ({
  open,
  onOpenChange,
  paymentData,
  onSubmit,
  loading,
}) => {
  const [formData, setFormData] = useState({
    amountPaid: "",
    paymentMode: "",
    transactionId: "",
    remarks: "",
    academicYear: "", // New field for academic year selection
  });

  const [errors, setErrors] = useState<string[]>([]);
  const [yearwiseFeeData, setYearwiseFeeData] = useState<YearwiseFeeData | null>(null);
  const [loadingYearwise, setLoadingYearwise] = useState(false);
  const [selectedYearData, setSelectedYearData] = useState<YearwiseFeeBreakdown | null>(null);

  const paymentModes = [
    { value: "cash", label: "Cash" },
    { value: "online", label: "Online Transfer" },
    { value: "card", label: "Card Payment" },
    { value: "cheque", label: "Cheque" },
    { value: "dd", label: "Demand Draft" },
  ];

  // Fetch year-wise fee breakdown when dialog opens
  useEffect(() => {
    if (paymentData?.studentId && open) {
      fetchYearwiseFeeBreakdown();
    }
  }, [paymentData?.studentId, open]);

  // Update selected year data when academic year changes
  useEffect(() => {
    if (formData.academicYear && yearwiseFeeData) {
      const selectedYear = yearwiseFeeData.yearwiseBreakdown.find(
        year => year.academicYear === formData.academicYear
      );
      setSelectedYearData(selectedYear || null);
    } else {
      setSelectedYearData(null);
    }
  }, [formData.academicYear, yearwiseFeeData]);

  useEffect(() => {
    if (paymentData) {
      setFormData({
        amountPaid: "",
        paymentMode: "",
        transactionId: "",
        remarks: "",
        academicYear: "",
      });
      setErrors([]);
      setYearwiseFeeData(null);
      setSelectedYearData(null);
    }
  }, [paymentData]);

  const fetchYearwiseFeeBreakdown = async () => {
    if (!paymentData?.studentId) return;
    
    setLoadingYearwise(true);
    try {
      const response = await api.get(`/fees/students/${paymentData.studentId}/yearwise-breakdown`);
      
      if (response.data && response.data.success) {
        setYearwiseFeeData(response.data.data);
        
        // Auto-select first available academic year if available
        if (response.data.data.availableAcademicYears.length > 0) {
          setFormData(prev => ({
            ...prev,
            academicYear: response.data.data.availableAcademicYears[0].academicYear
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching year-wise fee breakdown:', error);
    } finally {
      setLoadingYearwise(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const validateForm = () => {
    const newErrors: string[] = [];

    if (!formData.amountPaid || parseFloat(formData.amountPaid) <= 0) {
      newErrors.push("Amount paid must be greater than 0");
    }

    // Validate against selected academic year or total due
    const paymentAmount = parseFloat(formData.amountPaid);
    
    if (selectedYearData) {
      // Use selected year validation
      if (paymentAmount > selectedYearData.dueAmount) {
        newErrors.push(
          `Amount paid cannot exceed ${selectedYearData.academicYear} due amount of ${selectedYearData.formattedDueAmount}`
        );
      }
    } else if (yearwiseFeeData) {
      // Use total due validation
      if (paymentAmount > yearwiseFeeData.totals.totalDueAmount) {
        newErrors.push(
          `Amount paid cannot exceed total due amount of ${yearwiseFeeData.totals.formattedTotalDueAmount}`
        );
      }
    } else {
      // Fallback to original validation for legacy students
      const totalDue = paymentData?.dueAmount || 0;
      if (paymentAmount > totalDue) {
        newErrors.push(
          `Amount paid cannot exceed total due amount of ${formatCurrency(totalDue)}`
        );
      }
    }

    if (!formData.paymentMode) {
      newErrors.push("Payment mode is required");
    }

    if (formData.paymentMode !== "cash" && !formData.transactionId.trim()) {
      newErrors.push("Transaction ID is required for non-cash payments");
    }

    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    const submitData = {
      ...paymentData,
      ...formData,
      amountPaid: parseFloat(formData.amountPaid),
      // Ensure academicYear is included in the submission
      ...(formData.academicYear && { academicYear: formData.academicYear }),
    };
    onSubmit(submitData);
  };

  if (!paymentData) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-200 flex-shrink-0">
          <DialogTitle>
            Record New Payment
          </DialogTitle>
          <DialogDescription>
            Enter payment details for {yearwiseFeeData?.student ? `${yearwiseFeeData.student.firstName} ${yearwiseFeeData.student.lastName}` : paymentData.studentName}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              {/* Student Information */}
              <Card className="bg-gray-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="h-4 w-4" />
                    Student Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">Name:</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {yearwiseFeeData?.student ? `${yearwiseFeeData.student.firstName} ${yearwiseFeeData.student.lastName}` : paymentData.studentName}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">Roll Number:</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {yearwiseFeeData?.student?.rollNumber || paymentData.rollNumber}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">Father Name:</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {yearwiseFeeData?.student?.fatherName || paymentData.fatherName}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">Course:</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {yearwiseFeeData?.student?.courseGroup?.name || paymentData.courseGroup}
                      </span>
                    </div>
                    {yearwiseFeeData?.student?.courseGroup && (
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Duration:</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {yearwiseFeeData.student.courseGroup.durationYears} years
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Year-wise Fee Breakdown */}
              {loadingYearwise ? (
                <Card className="bg-blue-50">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-6 w-6 animate-spin mr-2" />
                      Loading year-wise fee breakdown...
                    </div>
                  </CardContent>
                </Card>
              ) : yearwiseFeeData ? (
                <>
                  {/* Fee Breakdown Summary */}
                  <Card className="bg-blue-50">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BookOpen className="h-4 w-4" />
                        Year-wise Fee Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        {yearwiseFeeData.yearwiseBreakdown.map((year) => (
                          <Card key={year.yearNumber} className="bg-white">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-sm flex items-center justify-between">
                                <span>Year {year.yearNumber}</span>
                                <Badge className={getStatusColor(year.status)}>
                                  {year.status}
                                </Badge>
                              </CardTitle>
                              <p className="text-xs text-gray-600">{year.academicYear}</p>
                            </CardHeader>
                            <CardContent className="pt-0">
                              <div className="space-y-1 text-xs">
                                <div className="flex justify-between">
                                  <span>Fee:</span>
                                  <span className="font-medium">{year.formattedFinalAmount}</span>
                                </div>
                                <div className="flex justify-between text-green-600">
                                  <span>Paid:</span>
                                  <span className="font-medium">{year.formattedPaidAmount}</span>
                                </div>
                                <div className="flex justify-between text-red-600 font-semibold">
                                  <span>Due:</span>
                                  <span>{year.formattedDueAmount}</span>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>

                      {/* Totals Summary */}
                      <div className="bg-white p-4 rounded-lg border">
                        <h4 className="text-sm font-semibold text-gray-800 mb-3">Program Totals</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div className="text-center">
                            <div className="text-gray-600">Total Fee</div>
                            <div className="font-semibold">{yearwiseFeeData.totals.formattedTotalFinalAmount}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-600">Total Paid</div>
                            <div className="font-semibold text-green-600">{yearwiseFeeData.totals.formattedTotalPaidAmount}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-600">Total Due</div>
                            <div className="font-semibold text-red-600">{yearwiseFeeData.totals.formattedTotalDueAmount}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-600">Discount</div>
                            <div className="font-semibold text-blue-600">{yearwiseFeeData.totals.formattedTotalDiscountAmount}</div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                /* Fallback for legacy students without year-wise fee data */
                <Card className="bg-blue-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Financial Summary (Legacy)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Total Fee:</span>
                        <span className="text-sm font-semibold text-gray-900">{formatCurrency(paymentData.totalFee)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Total Paid:</span>
                        <span className="text-sm font-semibold text-green-600">{formatCurrency(paymentData.paidAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Total Due:</span>
                        <span className={`text-sm font-semibold ${paymentData.dueAmount > 0 ? "text-red-600" : "text-green-600"}`}>
                          {formatCurrency(paymentData.dueAmount)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Payment Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center space-x-2">
                  <CreditCard className="h-4 w-4" />
                  <span>Payment Details</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Academic Year Selection */}
                  {yearwiseFeeData && yearwiseFeeData.availableAcademicYears.length > 0 && (
                    <div className="md:col-span-2">
                      <label htmlFor="academicYear" className="block text-sm font-medium text-gray-700 mb-1">
                        Academic Year (Payment For) *
                      </label>
                      <Select value={formData.academicYear} onValueChange={(value) => handleInputChange("academicYear", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select academic year for payment" />
                        </SelectTrigger>
                        <SelectContent>
                          {yearwiseFeeData.availableAcademicYears.map((year) => (
                            <SelectItem key={year.academicYear} value={year.academicYear}>
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                <span>Year {year.yearNumber} - {year.academicYear}</span>
                                <span className="text-sm text-gray-500">({year.formattedDueAmount} due)</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedYearData && (
                        <p className="text-xs text-blue-600 mt-1">
                          Selected: {selectedYearData.academicYear} (Year {selectedYearData.yearNumber}) - Due: {selectedYearData.formattedDueAmount}
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <label htmlFor="amountPaid" className="block text-sm font-medium text-gray-700 mb-1">
                      Amount Paid (₹) *
                    </label>
                    <Input
                      id="amountPaid"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Enter amount"
                      value={formData.amountPaid}
                      onChange={(e) => handleInputChange("amountPaid", e.target.value)}
                      className="text-lg font-semibold"
                    />
                  </div>

                  <div>
                    <label htmlFor="paymentMode" className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Mode *
                    </label>
                    <Select value={formData.paymentMode} onValueChange={(value) => handleInputChange("paymentMode", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select payment mode" />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentModes.map((mode) => (
                          <SelectItem key={mode.value} value={mode.value}>
                            {mode.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label htmlFor="transactionId" className="block text-sm font-medium text-gray-700 mb-1">
                      Transaction ID {formData.paymentMode !== "cash" && "*"}
                    </label>
                    <div className="relative">
                      <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        id="transactionId"
                        placeholder="Enter transaction/reference ID"
                        value={formData.transactionId}
                        onChange={(e) => handleInputChange("transactionId", e.target.value)}
                        className="pl-10"
                        disabled={formData.paymentMode === "cash"}
                      />
                    </div>
                    {formData.paymentMode === "cash" && (
                      <p className="text-xs text-gray-500 mt-1">Not required for cash payments</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="remarks" className="block text-sm font-medium text-gray-700 mb-1">
                      Remarks (Optional)
                    </label>
                    <Input
                      id="remarks"
                      placeholder="Additional notes"
                      value={formData.remarks}
                      onChange={(e) => handleInputChange("remarks", e.target.value)}
                    />
                  </div>
                </div>

                {/* Payment Summary */}
                {formData.amountPaid && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">
                        Remaining Due After Payment:
                      </span>
                      <span className="text-lg font-bold text-gray-900">
                        {(() => {
                          const paymentAmount = parseFloat(formData.amountPaid || "0");
                          let currentDue = 0;
                          
                          if (selectedYearData) {
                            currentDue = selectedYearData.dueAmount;
                          } else if (yearwiseFeeData) {
                            currentDue = yearwiseFeeData.totals.totalDueAmount;
                          } else {
                            currentDue = paymentData?.dueAmount || 0;
                          }
                          
                          return formatCurrency(Math.max(0, currentDue - paymentAmount));
                        })()}
                      </span>
                    </div>
                    {(() => {
                      const paymentAmount = parseFloat(formData.amountPaid || "0");
                      let currentDue = 0;
                      let isYearSpecific = false;
                      
                      if (selectedYearData) {
                        currentDue = selectedYearData.dueAmount;
                        isYearSpecific = true;
                      } else if (yearwiseFeeData) {
                        currentDue = yearwiseFeeData.totals.totalDueAmount;
                      } else {
                        currentDue = paymentData?.dueAmount || 0;
                      }
                      
                      return paymentAmount >= currentDue && (
                        <div className="flex items-center space-x-2 mt-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-green-700 font-medium">
                            {isYearSpecific
                              ? `This payment will fully clear ${selectedYearData?.academicYear} (Year ${selectedYearData?.yearNumber}) dues!`
                              : yearwiseFeeData
                              ? "This payment will fully clear all outstanding dues!"
                              : "This payment will fully clear the student's dues!"}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>

            {errors.length > 0 && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-2">
                    <div className="text-red-600">⚠️</div>
                    <div>
                      <p className="text-sm font-medium text-red-800">Please fix the following errors:</p>
                      <ul className="mt-1 text-sm text-red-700 list-disc list-inside">
                        {errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </form>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <div className="flex justify-end space-x-3 w-full">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !formData.amountPaid || !formData.paymentMode}
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleSubmit}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Recording Payment...
                </>
              ) : (
                <>
                  <IndianRupee className="h-4 w-4 mr-2" />
                  Record Payment
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentFormDialog;
